import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { 
  Search, Users, Briefcase, FileText, DollarSign, Calendar,
  Truck, Package, Settings, BarChart2, UserPlus, Printer,
  ClipboardList, Star, Tag, Building2, CreditCard, X,
  Command
} from 'lucide-react';
import axios from 'axios';
import { getAuthHeader } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// قائمة الصفحات للبحث السريع
const PAGES = [
  { path: '/dashboard', label: 'لوحة التحكم', icon: BarChart2, keywords: ['dashboard', 'home', 'الرئيسية'] },
  { path: '/clients', label: 'العملاء', icon: Users, keywords: ['clients', 'customers', 'زبائن'] },
  { path: '/projects', label: 'المشاريع', icon: Briefcase, keywords: ['projects', 'مشروع'] },
  { path: '/invoices', label: 'الفواتير', icon: FileText, keywords: ['invoices', 'bills', 'فاتورة'] },
  { path: '/expenses', label: 'المصاريف', icon: DollarSign, keywords: ['expenses', 'costs', 'مصروف'] },
  { path: '/employees', label: 'الموظفين', icon: Users, keywords: ['employees', 'staff', 'موظف'] },
  { path: '/calendar', label: 'التقويم', icon: Calendar, keywords: ['calendar', 'مواعيد', 'جدول'] },
  { path: '/delivery', label: 'التوصيل', icon: Truck, keywords: ['delivery', 'توصيل', 'شحن'] },
  { path: '/drivers-management', label: 'إدارة السائقين', icon: Truck, keywords: ['drivers', 'سائق'] },
  { path: '/inventory', label: 'المخزون', icon: Package, keywords: ['inventory', 'stock', 'مستودع'] },
  { path: '/print-orders', label: 'أوامر الطباعة', icon: Printer, keywords: ['print', 'طباعة'] },
  { path: '/quotations', label: 'عروض الأسعار', icon: ClipboardList, keywords: ['quotations', 'عرض سعر'] },
  { path: '/contracts', label: 'العقود', icon: FileText, keywords: ['contracts', 'عقد'] },
  { path: '/leads', label: 'العملاء المحتملين', icon: UserPlus, keywords: ['leads', 'prospects'] },
  { path: '/designs', label: 'التصاميم', icon: Star, keywords: ['designs', 'تصميم'] },
  { path: '/offers', label: 'العروض', icon: Tag, keywords: ['offers', 'discounts', 'خصم'] },
  { path: '/suppliers', label: 'الموردين', icon: Building2, keywords: ['suppliers', 'مورد'] },
  { path: '/payments', label: 'المدفوعات', icon: CreditCard, keywords: ['payments', 'دفع'] },
  { path: '/users-management', label: 'إدارة المستخدمين', icon: Users, keywords: ['users', 'مستخدم'] },
  { path: '/company-settings', label: 'إعدادات الشركة', icon: Settings, keywords: ['settings', 'إعدادات'] },
  { path: '/approvals', label: 'الموافقات', icon: ClipboardList, keywords: ['approvals', 'موافقة'] },
  { path: '/analytics', label: 'التحليلات', icon: BarChart2, keywords: ['analytics', 'تحليل', 'إحصائيات'] },
];

const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ pages: [], clients: [], projects: [], invoices: [] });
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Keyboard shortcut (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when opened
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Search function
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults({ pages: PAGES.slice(0, 6), clients: [], projects: [], invoices: [] });
      return;
    }

    const q = searchQuery.toLowerCase();
    
    // Search pages
    const matchedPages = PAGES.filter(page => 
      page.label.toLowerCase().includes(q) ||
      page.keywords.some(k => k.includes(q))
    );

    setResults(prev => ({ ...prev, pages: matchedPages }));

    // Search in database
    if (searchQuery.length >= 2) {
      setLoading(true);
      try {
        const [clientsRes, projectsRes, invoicesRes] = await Promise.all([
          axios.get(`${API}/api/clients?search=${searchQuery}&limit=5`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/projects?search=${searchQuery}&limit=5`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/invoices?search=${searchQuery}&limit=5`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
        ]);

        setResults(prev => ({
          ...prev,
          clients: clientsRes.data?.slice?.(0, 5) || [],
          projects: projectsRes.data?.slice?.(0, 5) || [],
          invoices: invoicesRes.data?.slice?.(0, 5) || []
        }));
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      performSearch(query);
    }, 300);
    return () => clearTimeout(debounce);
  }, [query, performSearch]);

  // Navigate to result
  const handleSelect = (path) => {
    navigate(path);
    setOpen(false);
    setQuery('');
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    const totalResults = results.pages.length + results.clients.length + results.projects.length + results.invoices.length;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % totalResults);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + totalResults) % totalResults);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Select current item
      let currentIndex = 0;
      for (const page of results.pages) {
        if (currentIndex === selectedIndex) {
          handleSelect(page.path);
          return;
        }
        currentIndex++;
      }
      for (const client of results.clients) {
        if (currentIndex === selectedIndex) {
          handleSelect(`/clients?id=${client.id}`);
          return;
        }
        currentIndex++;
      }
    }
  };

  return (
    <>
      {/* Search Trigger Button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        data-testid="global-search-btn"
      >
        <Search size={16} />
        <span className="hidden sm:inline">بحث سريع...</span>
        <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-white dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">
          <Command size={12} />K
        </kbd>
      </button>

      {/* Search Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl p-0 gap-0 dark:bg-gray-900 dark:border-gray-700">
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b dark:border-gray-700">
            <Search size={20} className="text-gray-400" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="ابحث عن صفحة، عميل، مشروع، فاتورة..."
              className="border-0 focus-visible:ring-0 text-lg dark:bg-gray-900 dark:text-white"
              data-testid="global-search-input"
            />
            {query && (
              <button onClick={() => setQuery('')} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                <X size={16} />
              </button>
            )}
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto p-2">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              </div>
            )}

            {!loading && (
              <>
                {/* Pages */}
                {results.pages.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 px-3 py-2 font-medium">الصفحات</p>
                    {results.pages.map((page, index) => {
                      const Icon = page.icon;
                      return (
                        <button
                          key={page.path}
                          onClick={() => handleSelect(page.path)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-right transition-colors ${
                            selectedIndex === index 
                              ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' 
                              : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                          }`}
                        >
                          <Icon size={18} className="text-gray-400" />
                          <span className="dark:text-white">{page.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Clients */}
                {results.clients.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 px-3 py-2 font-medium">العملاء</p>
                    {results.clients.map((client) => (
                      <button
                        key={client.id}
                        onClick={() => handleSelect(`/clients`)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-right hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      >
                        <Users size={18} className="text-green-500" />
                        <div className="flex-1">
                          <p className="font-medium dark:text-white">{client.name}</p>
                          <p className="text-xs text-gray-500">{client.phone}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Projects */}
                {results.projects.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 px-3 py-2 font-medium">المشاريع</p>
                    {results.projects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => handleSelect(`/projects`)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-right hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      >
                        <Briefcase size={18} className="text-purple-500" />
                        <div className="flex-1">
                          <p className="font-medium dark:text-white">{project.name}</p>
                          <p className="text-xs text-gray-500">{project.client_name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* No results */}
                {!loading && query && results.pages.length === 0 && results.clients.length === 0 && results.projects.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Search size={40} className="mx-auto mb-2 opacity-30" />
                    <p>لا توجد نتائج لـ "{query}"</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">↑↓</kbd>
                للتنقل
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">Enter</kbd>
                للاختيار
              </span>
            </div>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">Esc</kbd>
              للإغلاق
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GlobalSearch;
